'use client';

import type { MouseEvent } from 'react';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Button } from '@/ui-components/atoms/Button/Button';
import { Link } from '@/ui-components/atoms/Link/Link';
import {
    InAppPurchasePopover,
    type InAppPurchasePopoverVariant,
} from '@/src/ui-components/organisms/Snapshot/InAppPurchasePopover/InAppPurchasePopover';
import { buildCareUrlFromBaseUrl } from '@/services/careUrls';
import { getCareBaseUrl } from '@/services/runtimeConfig';
import styles from './partnerSubscription.module.scss';

const { ctaSection, textGroupSection, chatLinkStyles } = styles;

const TESLA_SUBSCRIBE_PATH = '/acquisitions-self-pay/ingest/vehicle?vehicleMake=Tesla&promoCode=TESLA24FOR10';
const CARE_FORGOT_PASSWORD_PATH = '/account/credentials/forgot-password?';

export type PartnerSubscriptionVariant = 'tesla' | 'roku' | 'apple';

interface SimplePartnerSubscriptionContent {
    body: string;
    chatLabel: string;
    ctaDataCy?: string;
    ctaHref?: string;
    ctaLabel?: string;
    title: string;
}

interface PartnerSubscriptionProps {
    variant?: PartnerSubscriptionVariant;
}

function isSimplePartnerSubscriptionVariant(
    variant: PartnerSubscriptionVariant,
): variant is Exclude<PartnerSubscriptionVariant, 'apple'> {
    return variant in partnerSubscriptionContent;
}

const partnerSubscriptionContent: Record<
    Exclude<PartnerSubscriptionVariant, 'apple'>,
    Omit<SimplePartnerSubscriptionContent, 'ctaHref'>
> = {
    tesla: {
        title: "Looks like you're in a SiriusXM Tesla trial, or your trial expired",
        body: 'To continue enjoying SiriusXM, you can purchase a plan and then return to manage your account.',
        ctaLabel: 'Subscribe now',
        ctaDataCy: 'subscribe-to-tesla-offer',
        chatLabel: 'Or, chat with an agent for help',
    },
    roku: {
        title: 'Looks like your SiriusXM subscription is connected through Roku.',
        body: 'To manage or update that subscription, use Roku billing or chat with an agent for additional help.',
        chatLabel: 'Chat with an agent for help',
    },
};

const partnerInAppPurchaseVariants: Partial<Record<PartnerSubscriptionVariant, InAppPurchasePopoverVariant>> = {
    apple: 'apple-managed',
};

function openChat(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    const clientWindow = window as BrowserWindow;
    clientWindow?.sierraSXM?.openChatModal();
}

export default function PartnerSubscription({ variant = 'tesla' }: PartnerSubscriptionProps): React.JSX.Element {
    const careBaseUrl = getCareBaseUrl();
    const inAppPurchaseVariant = partnerInAppPurchaseVariants[variant];
    const forgotPasswordUrl = buildCareUrlFromBaseUrl(careBaseUrl, CARE_FORGOT_PASSWORD_PATH);

    if (inAppPurchaseVariant) {
        return (
            <InAppPurchasePopover
                chatClickHandler={openChat}
                embedded={true}
                forgotPasswordHref={forgotPasswordUrl}
                variant={inAppPurchaseVariant}
            />
        );
    }

    if (!isSimplePartnerSubscriptionVariant(variant)) {
        return <></>;
    }

    const content: SimplePartnerSubscriptionContent =
        variant === 'tesla'
            ? {
                  ...partnerSubscriptionContent.tesla,
                  ctaHref: buildCareUrlFromBaseUrl(careBaseUrl, TESLA_SUBSCRIBE_PATH),
              }
            : partnerSubscriptionContent[variant];

    return (
        <>
            <div className={textGroupSection}>
                <Text tag="h6" variant="h6">
                    {content.title}
                </Text>
                <Text tag="p" variant="text-medium">
                    {content.body}
                </Text>
            </div>
            <div className={ctaSection}>
                {content.ctaHref && content.ctaLabel ? (
                    <Button
                        type="button"
                        ariaLabel={content.ctaLabel.toLowerCase()}
                        variant="primary"
                        size="small"
                        fitting="fill-container"
                        dataCy={content.ctaDataCy}
                        href={content.ctaHref}
                    >
                        {content.ctaLabel}
                    </Button>
                ) : null}
                <Link className={chatLinkStyles} href="" onClick={openChat}>
                    {content.chatLabel}
                </Link>
            </div>
        </>
    );
}